import { useState, useEffect } from 'react';
import {
  Card,
  CardContent,
  Typography,
  Box,
  IconButton,
  Collapse,
  Stack,
  Divider,
  alpha,
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import PlayArrowIcon from '@mui/icons-material/PlayArrow';
import CalendarTodayIcon from '@mui/icons-material/CalendarToday';
import Badge from '@/components/common/Badge';
import { highlightText } from '@/lib/highlight';
import type { AggregatedDate, ViewpointEntry, ReferenceItem } from '@/types';
import ReferencesModal from './ReferencesModal';

interface DateCardProps {
  data: AggregatedDate;
  ticker: string;
  isChannelPage?: boolean;  // When true, show ticker instead of author in entries
  forceDateExpanded?: boolean;
  dateExpandVersion?: number;
  forceSentimentExpanded?: boolean;
  sentimentExpandVersion?: number;
}

// Slot order for sorting: 盘前 < 盘中 < 盘后 < null/empty
const SLOT_ORDER: Record<string, number> = {
  '盘前': 0,
  '盘中': 1,
  '盘后': 2,
};

// Sentiment section colors
const SENTIMENT_COLORS = {
  bull: '#2AA84A',
  neutral: '#9AA4B2',
  bear: '#FF6A00',
};

// Format date from yyyy-mm-dd to more readable format
function formatDate(dateStr: string): string {
  const date = new Date(dateStr);
  const month = date.getMonth() + 1;
  const day = date.getDate();
  const weekdays = ['周日', '周一', '周二', '周三', '周四', '周五', '周六'];
  const weekday = weekdays[date.getDay()];
  return `${month}月${day}日 ${weekday}`;
}

interface SentimentSectionProps {
  title: string;
  entries: ViewpointEntry[];
  color: string;
  onOpenReferences: (refs: ReferenceItem[], ticker: string) => void;
  ticker: string;
  isChannelPage?: boolean;  // When true, show ticker instead of author
  forceExpanded?: boolean;
  expandVersion?: number;
}

function SentimentSection({ title, entries, color, onOpenReferences, ticker, isChannelPage, forceExpanded, expandVersion }: SentimentSectionProps) {
  const [expanded, setExpanded] = useState(true);

  useEffect(() => {
    if (expandVersion !== undefined && expandVersion > 0) {
      setExpanded(forceExpanded ?? true);
    }
  }, [expandVersion, forceExpanded]);

  if (entries.length === 0) return null;

  const sortedEntries = [...entries].sort((a, b) => {
    const orderA = SLOT_ORDER[a.slot || ''] ?? 99;
    const orderB = SLOT_ORDER[b.slot || ''] ?? 99;
    return orderA - orderB;
  });

  return (
    <Box sx={{ mt: 1 }}>
      <Box
        onClick={() => setExpanded(!expanded)}
        sx={{
          display: 'inline-flex',
          alignItems: 'center',
          gap: 0.5,
          px: 1.5,
          py: 0.5,
          mb: expanded ? -1.5 : 0,
          bgcolor: 'background.paper',
          border: '2px solid',
          borderColor: 'divider',
          borderRadius: 2,
          cursor: 'pointer',
          userSelect: 'none',
          position: 'relative',
          zIndex: 1,
          ml: 1,
        }}
      >
        <Box
          sx={{
            width: 8,
            height: 8,
            borderRadius: '50%',
            bgcolor: color,
          }}
        />
        <Typography variant="subtitle2" sx={{ fontWeight: 700, color: 'text.primary' }}>
          {title}
        </Typography>
        <Typography variant="caption" color="text.secondary">
          ({entries.length})
        </Typography>
        <ExpandMoreIcon
          fontSize="small"
          sx={{
            transform: expanded ? 'rotate(180deg)' : 'none',
            transition: 'transform 0.2s',
            color: 'text.secondary',
          }}
        />
      </Box>

      <Collapse in={expanded}>
        <Box
          sx={{
            position: 'relative',
            borderRadius: 3,
            border: '2px solid',
            borderColor: 'divider',
            bgcolor: 'background.paper',
            overflow: 'hidden',
          }}
        >
          <Box
            sx={{
              position: 'absolute',
              left: 0,
              top: 0,
              bottom: 0,
              width: 6,
              bgcolor: color,
            }}
          />
          <Box sx={{ pl: 3, pr: 2, pt: 2, pb: 2 }}>
            <Stack spacing={1.5}>
              {sortedEntries.map((entry, idx) => (
                <Box key={idx}>
                  <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1 }}>
                    {entry.slot && (
                      <Badge variant="gray" size="small">
                        {entry.slot}
                      </Badge>
                    )}
                    <Typography
                      variant="body2"
                      sx={{ fontWeight: 600, color: 'primary.main', flexShrink: 0 }}
                    >
                      {isChannelPage ? entry.ticker : `@${entry.author}`}
                    </Typography>
                    {entry.references.length > 0 && (
                      <IconButton
                        size="small"
                        onClick={() => onOpenReferences(entry.references, entry.ticker || ticker)}
                        sx={{ p: 0.25, color: 'primary.main' }}
                      >
                        <PlayArrowIcon fontSize="small" />
                      </IconButton>
                    )}
                  </Box>
                  <Typography
                    variant="body2"
                    sx={{ lineHeight: 1.6, mt: 0.5 }}
                    component="div"
                  >
                    {highlightText(entry.reasoning)}
                  </Typography>
                  {idx < sortedEntries.length - 1 && (
                    <Divider sx={{ mt: 1.5, opacity: 0.3 }} />
                  )}
                </Box>
              ))}
            </Stack>
          </Box>
        </Box>
      </Collapse>
    </Box>
  );
}

export default function DateCard({
  data,
  ticker,
  isChannelPage = false,
  forceDateExpanded,
  dateExpandVersion,
  forceSentimentExpanded,
  sentimentExpandVersion,
}: DateCardProps) {
  const [expanded, setExpanded] = useState(true);
  const [modalOpen, setModalOpen] = useState(false);
  const [modalRefs, setModalRefs] = useState<ReferenceItem[]>([]);

  useEffect(() => {
    if (dateExpandVersion !== undefined && dateExpandVersion > 0) {
      setExpanded(forceDateExpanded ?? true);
    }
  }, [dateExpandVersion, forceDateExpanded]);

  const handleOpenReferences = (refs: ReferenceItem[], _ticker: string) => {
    setModalRefs(refs);
    setModalOpen(true);
  };

  const hasBull = data.bull.length > 0;
  const hasNeutral = data.neutral.length > 0;
  const hasBear = data.bear.length > 0;

  return (
    <>
      <Card sx={{ mb: 2 }}>
        <CardContent sx={{ pb: expanded ? 2 : '16px !important' }}>
          <Box
            onClick={() => setExpanded(!expanded)}
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: 2,
              mb: expanded ? 1 : 0,
              flexWrap: 'wrap',
              cursor: 'pointer',
              userSelect: 'none',
            }}
          >
            <IconButton
              size="small"
              sx={{
                transform: expanded ? 'rotate(180deg)' : 'none',
                transition: 'transform 0.2s',
              }}
              onClick={(e) => {
                e.stopPropagation();
                setExpanded(!expanded);
              }}
            >
              <ExpandMoreIcon />
            </IconButton>

            {/* Date pill */}
            <Box
              sx={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: 1,
                px: 2,
                py: 0.75,
                borderRadius: 10,
                bgcolor: 'background.paper',
                border: '2px solid',
                borderColor: 'divider',
                boxShadow: 1,
              }}
            >
              <CalendarTodayIcon sx={{ fontSize: 18, color: 'text.secondary' }} />
              <Typography variant="h6" sx={{ fontWeight: 700 }}>
                {formatDate(data.date)}
              </Typography>
            </Box>

            {/* Stats chips */}
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 1,
                  px: 1.5,
                  py: 0.5,
                  borderRadius: 2,
                  bgcolor: alpha('#9AA4B2', 0.15),
                }}
              >
                <Box
                  sx={{
                    width: 8,
                    height: 8,
                    borderRadius: '50%',
                    bgcolor: SENTIMENT_COLORS.bull,
                  }}
                />
                <Typography variant="body2" color="text.secondary">
                  {isChannelPage
                    ? <>看涨 <strong>{data.bullTickers.size}个标的 ({data.tickerBullishRatio}%)</strong></>
                    : <>看涨 <strong>{data.bullAuthors.size}位 ({data.bullishRatio}%)</strong></>
                  }
                </Typography>
              </Box>
              <Typography variant="body2" color="text.secondary">
                {isChannelPage
                  ? `共 ${data.tickerCount} 个标的`
                  : `共 ${data.authorCount} 位博主`
                }
              </Typography>
            </Box>
          </Box>

          <Collapse in={expanded}>
            {hasBull && (
              <SentimentSection
                title="【看涨】"
                entries={data.bull}
                color={SENTIMENT_COLORS.bull}
                onOpenReferences={handleOpenReferences}
                ticker={ticker}
                isChannelPage={isChannelPage}
                forceExpanded={forceSentimentExpanded}
                expandVersion={sentimentExpandVersion}
              />
            )}
            {hasNeutral && (
              <SentimentSection
                title="【观望】"
                entries={data.neutral}
                color={SENTIMENT_COLORS.neutral}
                onOpenReferences={handleOpenReferences}
                ticker={ticker}
                isChannelPage={isChannelPage}
                forceExpanded={forceSentimentExpanded}
                expandVersion={sentimentExpandVersion}
              />
            )}
            {hasBear && (
              <SentimentSection
                title="【看跌】"
                entries={data.bear}
                color={SENTIMENT_COLORS.bear}
                onOpenReferences={handleOpenReferences}
                ticker={ticker}
                isChannelPage={isChannelPage}
                forceExpanded={forceSentimentExpanded}
                expandVersion={sentimentExpandVersion}
              />
            )}
          </Collapse>
        </CardContent>
      </Card>

      <ReferencesModal
        open={modalOpen}
        onClose={() => setModalOpen(false)}
        references={modalRefs}
        ticker={ticker}
      />
    </>
  );
}
